/**
 * S23 — Reports · Custom Report Builder (`/reports/builder`).
 * Compact drag-free builder: pick data sources, columns, filters,
 * preview and save.
 */
import React, { useState } from "react";
import { Hammer, Database, Filter, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { Flywheel } from "@/components/brand/Flywheel";
import { cn } from "@/lib/utils";

const SOURCES = [
  { id: "agents",       label: "Agents",         desc: "Inventory + risk band + autonomy", fields: ["name","owner","department","autonomy","riskScore","status","vendor","monthlyCostAud"] },
  { id: "policies",     label: "Policies",       desc: "Policy catalogue + violations",    fields: ["name","category","appliesTo","violations","severity","owner"] },
  { id: "incidents",    label: "Incidents",      desc: "Open + closed",                   fields: ["id","title","severity","status","owner","reported","category","agentName"] },
  { id: "requests",     label: "Deployment",     desc: "Approval workflow",                fields: ["id","agentName","department","risk","stage","slaDue","submittedAt"] },
  { id: "audit",        label: "Audit entries",  desc: "Tamper-evident ledger",            fields: ["ts","actor","action","entityType","entityRef","severity"] },
  { id: "vendors",      label: "Vendors",        desc: "Cost + latency + coverage",        fields: ["name","category","agentsGoverned","monthlySpendAud","latencyMs","status"] },
];

export default function ReportBuilder() {
  const { user } = useAuth();
  const { isCustomer } = useTenantData();
  const [src, setSrc] = useState("agents");
  const [fields, setFields] = useState(["name", "owner", "department"]);
  const [filter, setFilter] = useState("");
  if (isCustomer) return <Empty userName={user?.name?.split(" ")[0]} />;

  const source = SOURCES.find((s) => s.id === src);
  const toggle = (f) => setFields((cur) => cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]);

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="report-builder">
      <div className="absolute -right-24 -top-12 pointer-events-none"><Flywheel size={300} opacity={0.05} /></div>

      <p className="eyebrow">Reports · Custom Report Builder</p>
      <h1 className="text-display-md text-ink-primary mt-1">Custom Report Builder</h1>
      <p className="text-ink-secondary mt-1 max-w-2xl">Compose tenant-scoped reports across agents, policies, incidents, deployment, vendors and audit. Save as a one-off or schedule.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <section className="surface-card shadow-e1 p-4" data-testid="rb-sources">
          <p className="eyebrow flex items-center gap-1"><Database size={12} /> Data source</p>
          <ul className="mt-3 space-y-1.5">
            {SOURCES.map((s) => (
              <li key={s.id}>
                <button type="button" onClick={() => { setSrc(s.id); setFields(s.fields.slice(0, 3)); }} data-testid={`rb-source-${s.id}`}
                  className={cn("w-full text-left px-3 py-2 rounded-md border text-sm transition-colors",
                    src === s.id ? "border-tralenex-500 bg-tralenex-50/40 text-ink-primary" : "border-border-subtle text-ink-secondary hover:text-ink-primary")}>
                  <span className="font-medium block">{s.label}</span>
                  <span className="text-meta">{s.desc}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="surface-card shadow-e1 p-4" data-testid="rb-columns">
          <p className="eyebrow flex items-center gap-1"><Filter size={12} /> Columns</p>
          <p className="text-meta text-ink-secondary mt-0.5">Pick the fields to include for <span className="font-mono">{source.label}</span>.</p>
          <ul className="mt-3 grid grid-cols-1 gap-1.5">
            {source.fields.map((f) => (
              <li key={f}>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" data-testid={`rb-field-${f}`} checked={fields.includes(f)} onChange={() => toggle(f)} className="accent-tralenex-600" />
                  <span className="font-mono text-ink-primary">{f}</span>
                </label>
              </li>
            ))}
          </ul>
          <p className="eyebrow mt-4">Filter</p>
          <input type="text" placeholder="e.g. risk = high" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="rb-filter"
            className="mt-1 w-full h-9 px-2 rounded-md border border-border-subtle text-sm placeholder:text-ink-disabled focus:outline-none focus:border-tralenex-500" />
        </section>

        <section className="surface-card shadow-e1 p-4" data-testid="rb-preview">
          <p className="eyebrow flex items-center gap-1"><Eye size={12} /> Preview</p>
          <div className="mt-3 rounded-md border border-border-subtle overflow-hidden">
            <table className="w-full text-sm" data-testid="rb-preview-table">
              <thead className="bg-inset/40 text-ink-secondary text-meta uppercase tracking-[0.06em]">
                <tr>{fields.map((f) => <th key={f} className="px-2 py-1.5 text-left font-semibold">{f}</th>)}</tr>
              </thead>
              <tbody>
                {[1,2,3].map((i) => (
                  <tr key={i} className="border-t border-border-subtle">
                    {fields.map((f) => <td key={f} className="px-2 py-1.5 font-mono text-meta text-ink-secondary">{f}-{i.toString().padStart(3,'0')}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-meta text-ink-secondary mt-2">Sample rows only — generate to materialise the live data set.</p>
          <div className="mt-4 flex gap-2">
            <button type="button"
              onClick={() => toast.success("Report generated", { description: `${source.label} · ${fields.length} columns · saved to /reports/library.` })}
              data-testid="rb-generate"
              className="flex-1 inline-flex items-center justify-center gap-1 h-9 px-3 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"><Hammer size={14} /> Generate</button>
            <button type="button"
              onClick={() => toast.info("Saved as scheduled report", { description: "Visit /reports/scheduled to confirm the cadence." })}
              data-testid="rb-schedule"
              className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-md border border-border-subtle text-sm text-ink-secondary hover:text-ink-primary">Schedule…</button>
          </div>
        </section>
      </div>
    </div>
  );
}

function Empty({ userName }) {
  return (
    <div className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden" data-testid="rb-customer-empty">
      <div className="absolute -right-40 -bottom-40 pointer-events-none"><Flywheel size={620} opacity={0.04} /></div>
      <div className="relative max-w-2xl">
        <p className="eyebrow">Reports · Custom Report Builder</p>
        <h1 className="text-display-lg text-ink-primary mt-1">{userName ? `Welcome, ${userName}.` : "Welcome."}</h1>
        <p className="text-ink-secondary mt-3 text-[15px]">Bring data into ALGOS first — onboard agents and the builder unlocks every source.</p>
      </div>
    </div>
  );
}
